import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MICROPHONE_PROBLEM_HELP } from 'src/app/models/microphone-availability.model';
import { IncomingCallData } from 'src/app/services/communications/models/call.model';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
  selector: 'app-incoming-call-dialog',
  templateUrl: './incoming-call-dialog.component.html',
  styleUrls: ['./incoming-call-dialog.component.scss'],
})
export class IncomingCallDialogComponent {
  formattedCallerNumber: string;

  /**
   * Drives the "Post Discharge Call" chip.
   *
   * Resolved by `TwilioService` from the TwiML `callType` parameter before this dialog opens, so it
   * is a plain boolean: the label is on screen from the first frame, with nothing to load while the
   * phone is ringing and nothing to fail.
   */
  readonly isPostDischargeCall: boolean;

  /**
   * Drives the "Warm Transfer" chip and, under it, the clinic the call came from.
   *
   * Resolved the same way and for the same reason as the flag above: `TwilioService` reads the
   * TwiML `callType` before the dialog opens, so the nurse sees what kind of call is ringing from
   * the first frame rather than answering an unlabelled one.
   */
  readonly isWarmTransfer: boolean;
  readonly clinicName: string | null;

  /** Shown above the buttons when the microphone is known to be down. Answering stays allowed. */
  readonly microphoneWarning: string | null;

  constructor(
    public dialogRef: MatDialogRef<IncomingCallDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IncomingCallData,
    private utilsService: UtilsService
  ) {
    this.dialogRef.disableClose = true;
    this.formattedCallerNumber = this.utilsService.formatPhoneNumber(this.data.callerNumber);
    this.isPostDischargeCall = this.data.isPostDischargeCall === true;
    this.isWarmTransfer = this.data.isWarmTransfer === true;
    // Falsy to null so the template has one absence to test rather than two.
    this.clinicName = this.data.clinicName || null;
    this.microphoneWarning = this.data.microphoneUnavailable ? `${MICROPHONE_PROBLEM_HELP[this.data.microphoneUnavailable].title}.` : null;
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onAccept(): void {
    this.dialogRef.close({ action: 'accept', call: this.data.call });
  }

  onReject(): void {
    this.dialogRef.close({ action: 'reject', call: this.data.call });
  }
}
